import React, { useState, useEffect } from "react";
import { API_BASE } from "../api/config";
import { useParams, useNavigate } from "react-router-dom";
import { useTable } from "react-table";

function TrackMyKots() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const navigate = useNavigate();
  const { branch, tableName } = useParams();

  // Status mapping
  const statusMap = {
    1: "Placed",
    2: "Accepted",
    3: "Ready",
    4: "Served",
    5: "Cancel",
  };

  const statusColors = {
    Placed: "bg-yellow-500",
    Accepted: "bg-green-500",
    Ready: "bg-blue-500",
    Served: "bg-green-500",
    Cancel: "bg-red-500",
  };

  // Fetch KOT details for selected table
  const fetchKots = async () => {
    try {
      const url = `${API_BASE}?key=dzital_key&pswd=Hibujji@123&table=get_kot_details_by_table&transac=get_table_kot&table_name_url=${encodeURIComponent(
        tableName
      )}&condition=branch&branch=${encodeURIComponent(branch)}`;

      console.log("Fetching:", url);

      const response = await fetch(url);
      const text = await response.text();
      console.log("Raw response:", text);

      let result;
      try {
        result = JSON.parse(text);
      } catch (err) {
        console.error("Response not valid JSON:", err);
        setData([]); // Clear data if JSON parsing fails
        setLoading(false);
        return;
      }

      const rows = Array.isArray(result.data) ? result.data : [];
      if (!result || result.status !== "success") {
        console.error("Invalid API response format:", result);
        setData([]); // Clear data if API returns error
        setLoading(false);
        return;
      }

      const formattedData = rows.map((item, index) => ({
        sno: index + 1,
        table_name: item.table_name,
        item_name: item.item_name,
        item_qty: item.item_qty,
        kot_running_status: statusMap[item.kot_running_status] || "Unknown",
        raw_status: parseInt(item.kot_running_status, 10),
        raw_id: item.id,
      }));

      setData(formattedData); // Overwrite old data
      setLastUpdated(new Date().toLocaleTimeString());
      setLoading(false);
    } catch (error) {
      console.error("Error fetching KOTs:", error);
      setData([]); // Clear data on error
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKots();
    const interval = setInterval(fetchKots, 5000);
    return () => clearInterval(interval);
  }, [tableName, branch]);

  const columns = React.useMemo(
    () => [
      { Header: "S.No", accessor: "sno" },
      { Header: "Table", accessor: "table_name" },
      { Header: "Item", accessor: "item_name" },
      { Header: "Qty", accessor: "item_qty" },
      {
        Header: "Status",
        accessor: "kot_running_status",
        Cell: ({ value }) => (
          <span
            className={`px-2 py-1 text-xs rounded text-white ${
              statusColors[value] || "bg-gray-500"
            }`}
          >
            Order {value}
          </span>
        ),
      },
    ],
    []
  );

  const tableInstance = useTable({ columns, data });
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white bg-gradient-to-b from-blue-900 to-black">
        <p>Loading KOT details...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-900 to-black p-6 text-white text-sm">
      <h1 className="text-xl font-bold mb-4">
        KOT Details for Table: {tableName}
      </h1>

      {/* Last Updated + Order Again Button */}
      <div className="mb-2 flex justify-between items-center text-xs">
        <div className="text-gray-300">
          Last updated at: {lastUpdated || "—"}
        </div>
        <button
          onClick={() => navigate(`/order-self/${branch}/${tableName}`)}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg shadow-md"
        >
          Order Again
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-white bg-opacity-10 rounded mb-4">
        <table
          {...getTableProps()}
          className="min-w-full border border-gray-300 text-xs"
        >
          <thead className="bg-gray-200 bg-opacity-20 text-white">
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps()}
                    className="px-2 py-1 border cursor-pointer"
                  >
                    {column.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center py-4 text-gray-300"
                >
                  No KOTs for this table
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                prepareRow(row);
                return (
                  <tr
                    {...row.getRowProps()}
                    className="hover:bg-gray-100 hover:bg-opacity-20"
                  >
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()} className="px-2 py-1 border">
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default TrackMyKots;
